import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { mockOrders } from "@/mocks/orders";
import type { Order } from "@/mocks/orders";
import OrderDetailModal from "./components/OrderDetailModal";

const statusTabs = [
  { value: "all", label: "All Orders" },
  { value: "processing", label: "Processing" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const statusColor = (status: string) => {
  switch (status) {
    case "delivered": return "bg-accent-100 text-accent-800";
    case "shipped": return "bg-secondary-100 text-secondary-800";
    case "processing": return "bg-primary-100 text-primary-800";
    case "cancelled": return "bg-foreground-100 text-foreground-600";
    default: return "bg-background-200 text-foreground-500";
  }
};

export default function AdminOrdersPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isAr = i18n.language?.startsWith("ar");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Auto-open order detail when URL contains an order ID (e.g. from notification click)
  useEffect(() => {
    if (id) {
      const order = mockOrders.find((o) => o.id === id);
      if (order) {
        setSelectedOrder(order);
      }
    }
  }, [id]);

  const handleCloseModal = () => {
    setSelectedOrder(null);
    // Clear the URL param by navigating back to the base orders page
    if (id) {
      navigate("/admin/orders", { replace: true });
    }
  };

  const filteredOrders = useMemo(() => {
    return mockOrders.filter(o => {
      const matchesStatus = statusFilter === "all" || o.status === statusFilter;
      const matchesSearch = searchQuery === "" ||
        o.orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.shippingAddress.fullName.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [statusFilter, searchQuery]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: mockOrders.length };
    statusTabs.slice(1).forEach(st => {
      counts[st.value] = mockOrders.filter(o => o.status === st.value).length;
    });
    return counts;
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950">{t("admin.orders")}</h1>
          <p className="text-sm text-foreground-500 mt-1">Manage and track customer orders</p>
        </div>

        {/* Status tabs */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-1.5 flex flex-wrap gap-1">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                statusFilter === tab.value
                  ? "bg-primary-500 text-white"
                  : "text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70"
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 text-xs ${statusFilter === tab.value ? "text-white/70" : "text-foreground-400"}`}>
                ({statusCounts[tab.value] || 0})
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Search by order number or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
        </div>

        {/* Orders Table */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70">
                  {["Order #", "Customer", "Date", "Items", "Total", "Status", "Payment"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => {
                      setSelectedOrder(order);
                      navigate(`/admin/orders/${order.id}`, { replace: true });
                    }}
                    className="border-b border-background-100 hover:bg-background-100/50 transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">{order.orderNumber}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-700">{order.shippingAddress.fullName}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">{order.date}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-700 whitespace-nowrap">{order.items.length} item{order.items.length !== 1 ? "s" : ""}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">${order.total.toFixed(2)}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap ${statusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">
                        {order.paymentMethod.type} ····{order.paymentMethod.last4}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredOrders.length === 0 && (
            <div className="py-16 text-center">
              <i className="ri-inbox-line text-3xl text-foreground-300 mb-3 block"></i>
              <p className="text-sm text-foreground-500">No orders found</p>
            </div>
          )}
        </div>

        {/* Order Detail Modal */}
        {selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </AdminLayout>
  );
}